import React, { useEffect, useRef, useState } from 'react';
import AppScreen from './AppScreens';

/**
 * «Një ditë pune» — veçoritë e produktit si momente të një dite.
 *
 * Në desktop ora e madhe rri e ngrirë majtas dhe shifrat e saj rrotullohen kur
 * arrin momenti tjetër (07:58 → 10:30 → 13:15 → 30.10); poshtë saj, lista e
 * momenteve është edhe navigim. Në telefon secili moment e mban orën e vet.
 * Kjo është lëvizja e vetme e faqes jashtë skenës 3D; pa `prefers-reduced-motion`
 * shifrat ndërrohen pa animacion (CSS).
 *
 * Çdo moment e tregon ekranin e vet të app-it (`AppScreens.js`).
 *
 * Kohët dhe emrat janë ilustrim (të njëjtët njerëz si te skena); pikat janë
 * veçori të verifikuara në kod.
 */

function Clock({ value, label }) {
  // Çdo shifër ka çelësin e vet: rrotullohet vetëm ajo që ndryshoi.
  return (
    <p className="nxp-clock">
      <span className="nxp-sr">{label}: {value}</span>
      {value.split('').map((ch, i) => (
        <span key={`${i}-${ch}`} className={/\d/.test(ch) ? 'nxp-digit' : 'nxp-sep'} aria-hidden="true">{ch}</span>
      ))}
    </p>
  );
}

export default function DayTimeline({ day, onSectionLink, sectionHref }) {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    // Momenti aktiv është ai që kalon nëpër brezin e mesit të ekranit.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(Number(e.target.dataset.index));
      });
    }, { rootMargin: '-45% 0px -50% 0px' });
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [day]);

  const current = day.stages[active];

  return (
    <section id="si-funksionon" className="nxp-day" aria-labelledby="nxp-day-title">
      <div className="nxp-wrap nxp-day-grid">
        <div className="nxp-day-rail">
          <div className="nxp-day-sticky">
            <h2 id="nxp-day-title" className="nxp-h2" tabIndex={-1} data-section-heading>{day.title}</h2>
            <Clock value={current.time} label={day.clockLabel} />
            <p className="nxp-day-caption" aria-hidden="true">{current.caption}</p>
            <ol className="nxp-day-index">
              {day.stages.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={sectionHref(s.id)}
                    onClick={onSectionLink(s.id)}
                    aria-current={i === active ? 'step' : undefined}
                  >
                    <span className="nxp-day-index-time">{s.time}</span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <ol className="nxp-day-stages">
          {day.stages.map((s, i) => (
            <li
              key={s.id}
              id={s.id}
              ref={(el) => { refs.current[i] = el; }}
              data-index={i}
              className={`nxp-stage${i === active ? ' is-active' : ''}`}
              aria-labelledby={`nxp-${s.id}-title`}
            >
              <p className="nxp-stage-time">
                <span>{s.time}</span> {s.caption}
              </p>
              <h3 id={`nxp-${s.id}-title`} className="nxp-stage-title" tabIndex={-1} data-section-heading>{s.title}</h3>
              <p className="nxp-stage-lead">{s.lead}</p>
              <AppScreen id={s.id} s={day.screens} />
              <ul className="nxp-stage-points">
                {s.points.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
